/**
 * Returns false for image formats whose converter pipeline in zipper does
 * NOT actually apply the `crop` / `cropAspect` params — so exposing crop UI
 * for them would mislead the user (they'd pick a region and get back the
 * untouched full image).
 *
 * Current offenders:
 *   - PSD / PSB / PDD / PSDT → `zipper/Converter/delegates/psd` flattens
 *     layer 0 and ignores crop geometry.
 *   - AI → `zipper/Converter/delegates/ai-jpg|png|pdf` rasterise via
 *     ghostscript/imagemagick without honouring crop params.
 *   - EPS → `zipper/Converter/delegates/eps-png` does the same.
 *   - GIF / TIFF → `zipper/Converter/delegates/default` converts only
 *     frame / layer 0 (`layeredFormats`) and does not apply crop geometry.
 *
 * All other formats either go through the RAW delegate (crop applied via
 * sharp `.extract()`) or through the default delegate (browser-renderable
 * raster formats — crop can be wired up there when/if needed).
 *
 * The helper is intentionally tolerant: checks MIME type first and falls
 * back to the file extension when MIME is missing or generic.
 *
 * @param {string|undefined} mimeType
 * @param {string|undefined} [fileExtension]
 * @returns {boolean} true when the backend will honour a crop request
 */
const NON_CROPPABLE_EXTENSIONS = new Set([
  'psd', 'psb', 'pdd', 'psdt',
  'ai',
  'eps',
  'gif',
  'tif', 'tiff',
]);

const NON_CROPPABLE_MIMES = new Set([
  // PSD variants
  'image/vnd.adobe.photoshop',
  'application/x-photoshop',
  'application/photoshop',
  'application/psd',
  'image/photoshop',
  'image/psd',
  // Illustrator
  'application/illustrator',
  // EPS / PostScript (AI files are often served as postscript too)
  'application/postscript',
  'application/eps',
  'application/x-eps',
  'image/eps',
  'image/x-eps',
  // GIF / TIFF — first frame / layer 0 only conversion, crop is dropped
  'image/gif',
  'image/tiff',
  'image/x-tiff',
]);

export default function isCropSupportedFormat(mimeType, fileExtension) {
  if (fileExtension && typeof fileExtension === 'string') {
    const ext = fileExtension.toLowerCase().replace(/^\./, '').trim();
    if (ext && NON_CROPPABLE_EXTENSIONS.has(ext)) return false;
  }

  if (mimeType && typeof mimeType === 'string') {
    const m = mimeType.toLowerCase().trim();
    if (NON_CROPPABLE_MIMES.has(m)) return false;
  }

  return true;
}
